/**
 * DateRangePicker - Selector de rango de fechas
 * Usando MUI X Date Pickers
 */
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from '../../config/dayjs';

/**
 * @component DateRangePicker
 * @description Selector de rango de fechas con validación
 * @param {Object} props
 * @param {Date|null} props.startDate - Fecha de inicio
 * @param {Date|null} props.endDate - Fecha de fin
 * @param {Function} props.onChange - Callback al cambiar fechas: ({ startDate, endDate }) => void
 * @param {string} [props.startLabel='Fecha desde'] - Label para fecha inicio
 * @param {string} [props.endLabel='Fecha hasta'] - Label para fecha fin
 * @param {boolean} [props.disabled=false] - Deshabilitar selector
 * @param {Date} [props.minDate] - Fecha mínima permitida
 * @param {Date} [props.maxDate] - Fecha máxima permitida
 */
const DateRangePicker = ({
  startDate,
  endDate,
  onChange,
  startLabel = 'Fecha desde',
  endLabel = 'Fecha hasta',
  disabled = false,
  minDate,
  maxDate,
}) => {
  /**
   * Manejar cambio de fecha de inicio
   */
  const handleStartDateChange = (newDate) => {
    const start = newDate ? newDate.toDate() : null;

    // Validar que start <= end
    if (start && endDate && dayjs(start).isAfter(dayjs(endDate))) {
      // Si la nueva fecha de inicio es mayor que la de fin, ajustar la fecha de fin
      onChange({ startDate: start, endDate: start });
    } else {
      onChange({ startDate: start, endDate });
    }
  };

  /**
   * Manejar cambio de fecha de fin
   */
  const handleEndDateChange = (newDate) => {
    const end = newDate ? newDate.toDate() : null;

    // Validar que start <= end
    if (end && startDate && dayjs(end).isBefore(dayjs(startDate))) {
      // Si la nueva fecha de fin es menor que la de inicio, ajustar la fecha de inicio
      onChange({ startDate: end, endDate: end });
    } else {
      onChange({ startDate, endDate: end });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          width: '100%',
        }}
      >
        <DatePicker
          label={startLabel}
          value={startDate ? dayjs(startDate) : null}
          onChange={handleStartDateChange}
          disabled={disabled}
          minDate={minDate ? dayjs(minDate) : undefined}
          maxDate={maxDate ? dayjs(maxDate) : undefined}
          slotProps={{
            textField: {
              fullWidth: true,
              size: 'small',
            },
          }}
        />

        <DatePicker
          label={endLabel}
          value={endDate ? dayjs(endDate) : null}
          onChange={handleEndDateChange}
          disabled={disabled}
          minDate={startDate ? dayjs(startDate) : minDate ? dayjs(minDate) : undefined}
          maxDate={maxDate ? dayjs(maxDate) : undefined}
          slotProps={{
            textField: {
              fullWidth: true,
              size: 'small',
            },
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};

DateRangePicker.propTypes = {
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  onChange: PropTypes.func.isRequired,
  startLabel: PropTypes.string,
  endLabel: PropTypes.string,
  disabled: PropTypes.bool,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
};

export default DateRangePicker;
